import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Btn, Icon, Pill, StoreBtn } from "../../shared/ui";
import { PHONE_QUERY, clamp, delay, useScrollLoop } from "../../shared/motion";
import { openMerchantStore } from "../../shared/lib/storeLink";
import wmUrl from "../../assets/brand/n-white.svg";
import { GridPhone } from "./GridPhone";
import { PendingStack } from "./PendingStack";
import { HeroStats } from "./HeroStats";
import { TOAST_FIRST_AMOUNT, loc, type Lang, type StackItem } from "./mock";
import "./hero.css";

const TOAST_HOLD = 1800;

interface ToastState {
  kind: "paid" | "reminded";
  amount: StackItem["amount"] | null;
  /** Bumped per toast: the new key remounts the element so the enter animation replays. */
  n: number;
  out: boolean;
}

// Landing v4 hero (prototype 124–184): copy + CTAs on the left; on the right the brand panel with
// the merchant app's «Shartnomalar» phone, the payment toast and the pending-confirmation stack;
// the stats row underneath. The phone, toast and stack are decorative (aria-hidden) — their story
// is told by the heading and the stats.
const Hero = () => {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === "ru" ? "ru" : "uz";

  const heroRef = useRef<HTMLElement>(null);
  const wmRef = useRef<HTMLImageElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const parARef = useRef<HTMLDivElement>(null);
  const parBRef = useRef<HTMLDivElement>(null);

  // --- toast, driven by the stack ---
  const [toast, setToast] = useState<ToastState>({ kind: "paid", amount: TOAST_FIRST_AMOUNT, n: 0, out: false });
  const holdRef = useRef(0);
  useEffect(() => () => clearTimeout(holdRef.current), []);
  const showToast = useCallback((it: StackItem) => {
    clearTimeout(holdRef.current);
    const paid = it.kind === "in";
    setToast((p) => ({ kind: paid ? "paid" : "reminded", amount: paid ? it.amount : null, n: p.n + 1, out: false }));
    holdRef.current = window.setTimeout(() => setToast((p) => ({ ...p, out: true })), TOAST_HOLD);
  }, []);

  // --- scroll parallax (prototype scroll loop, hero part) ---
  const mqRef = useRef<{ rm: MediaQueryList; ph: MediaQueryList } | null>(null);
  useEffect(() => {
    mqRef.current = {
      rm: window.matchMedia("(prefers-reduced-motion: reduce)"),
      ph: window.matchMedia(PHONE_QUERY),
    };
  }, []);
  useScrollLoop((y) => {
    const mq = mqRef.current;
    const hero = heroRef.current;
    const list = listRef.current;
    const wrap = wrapRef.current;
    if (!mq || mq.rm.matches || !hero || !list || !wrap) return;
    if (hero.getBoundingClientRect().bottom <= 0) return;
    const ph = mq.ph.matches;
    const hp = clamp(y / (hero.offsetHeight * 0.85), 0, 1);
    const max = Math.max(0, list.scrollHeight - wrap.clientHeight);
    list.style.transform = `translateY(${(-hp * max).toFixed(1)}px)`;
    if (phoneRef.current) phoneRef.current.style.transform = `translateY(${(-hp * (ph ? 24 : 40)).toFixed(1)}px)`;
    if (parARef.current) parARef.current.style.transform = `translateY(${(-hp * (ph ? 30 : 70)).toFixed(1)}px)`;
    if (parBRef.current) parBRef.current.style.transform = `translateY(${(-hp * (ph ? 60 : 140)).toFixed(1)}px)`;
    if (wmRef.current)
      wmRef.current.style.transform = `rotate(${(hp * 16).toFixed(2)}deg) translateY(${(hp * 40).toFixed(1)}px)`;
  });

  const toastCls = toast.n === 0 ? "toast" : `toast ${toast.out ? "ns-toast-out" : "ns-toast-in"}`;

  return (
    <section className="hero" id="hero" ref={heroRef} aria-labelledby="hero-title">
      <div className="wrap">
        <div className="hero-grid">
          <div className="hero-copy">
            <Pill tone="active" data-r style={delay(80)}>
              {t("hero.eyebrow")}
            </Pill>
            <h1 id="hero-title" className="h1" data-r style={delay(160)}>
              {t("hero.title_pre")}
              <em>{t("hero.title_accent")}</em>
              {t("hero.title_post")}
            </h1>
            <p className="lead" data-r style={delay(260)}>
              {t("hero.subtitle")}
            </p>
            <div className="cta-row" data-r style={delay(360)}>
              <Btn
                as="a"
                float
                href="#download"
                onClick={(e) => {
                  e.preventDefault();
                  openMerchantStore();
                }}
              >
                {t("nav.cta")}
                <Icon name="i-arrowR" />
              </Btn>
              <StoreBtn store="apple" tone="secondary" />
              <StoreBtn store="play" tone="secondary" />
            </div>
          </div>

          <div className="stage">
            <div className="panel" data-r="scale" style={delay(120)}>
              <img className="wm" src={wmUrl} alt="" ref={wmRef} />
              <GridPhone phoneRef={phoneRef} listRef={listRef} wrapRef={wrapRef} />
            </div>

            <div className="par-a" ref={parARef} aria-hidden="true">
              <div data-r="drop" style={delay(900)}>
                <div className="float">
                  <div key={toast.n} className={toastCls}>
                    <span className="disc">
                      <Icon name="i-check" />
                    </span>
                    <span className="tmsg">
                      {t(toast.kind === "paid" ? "hero.mock.toast_paid" : "hero.stack.toast_reminded")}
                    </span>
                    {toast.amount && <b>+ {loc(toast.amount, lang)}</b>}
                  </div>
                </div>
              </div>
            </div>

            <div className="par-b" ref={parBRef} aria-hidden="true">
              <div data-r="pop" style={delay(1150)}>
                <div className="float" style={{ animationDelay: "-3s" }}>
                  <PendingStack hostRef={heroRef} onToast={showToast} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <HeroStats />
      </div>
    </section>
  );
};

export default Hero;
